import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  MapPin, Clock, ClipboardList, Link2, Lock, Key, Ban, Timer,
  XCircle, Wifi, CheckCircle, AlertTriangle, PauseCircle, Gem,
  Shield, Zap, Star, Flame,
} from 'lucide-react';
import { useLocation } from '../hooks/useLocation';
import { tg } from '../hooks/useTelegram';
import RippleButton from '../lib/RippleButton';
import { apiFetch, API_BASE, waitForInitData } from '../lib/api';
import { headerCache } from '../lib/headerCache';
import { formatGeo } from '../lib/geo';
import { C, G } from '../lib/design';
import { useLanguage } from '../contexts/LanguageContext';

const clog  = console.log.bind(console);
const cwarn = console.warn.bind(console);
const cerr  = console.error.bind(console);

// ─── Rarity config ────────────────────────────────────────────────────────────

const RARITY = {
  common:    { label: 'Common',    color: '#9CA3AF', glow: 'rgba(156,163,175,0.30)', bg: 'rgba(156,163,175,0.08)', grad: 'linear-gradient(135deg,#374151,#9CA3AF)',    Icon: Shield },
  rare:      { label: 'Rare',      color: '#60A5FA', glow: 'rgba(96,165,250,0.40)',  bg: 'rgba(96,165,250,0.08)',  grad: 'linear-gradient(135deg,#1D4ED8,#60A5FA)',    Icon: Star   },
  epic:      { label: 'Epic',      color: '#C084FC', glow: 'rgba(192,132,252,0.45)', bg: 'rgba(192,132,252,0.08)', grad: 'linear-gradient(135deg,#7C3AED,#C084FC)',    Icon: Gem    },
  legendary: { label: 'Legendary', color: '#FBBF24', glow: 'rgba(251,191,36,0.55)', bg: 'rgba(251,191,36,0.08)',  grad: 'linear-gradient(135deg,#B45309,#FBBF24)',    Icon: Zap    },
};

// ─── Error maps (keys only — resolved via t() in render) ─────────────────────

const ERRORS = {
  TOO_FAR:            { Icon: MapPin,        titleKey: 'err.TOO_FAR.title',            textKey: 'err.TOO_FAR.text' },
  TOO_SOON:           { Icon: Clock,         titleKey: 'err.TOO_SOON.title',           textKey: 'err.TOO_SOON.text' },
  NO_ACTIVE_CAMPAIGN: { Icon: ClipboardList, titleKey: 'err.NO_ACTIVE_CAMPAIGN.title', textKey: 'err.NO_ACTIVE_CAMPAIGN.text' },
  INVALID_QR_TOKEN:   { Icon: Link2,         titleKey: 'err.INVALID_QR_TOKEN.title',   textKey: 'err.INVALID_QR_TOKEN.text' },
  PIN_REQUIRED:       { Icon: Lock,          titleKey: 'err.PIN_REQUIRED.title',       textKey: 'err.PIN_REQUIRED.text' },
  INVALID_PIN:        { Icon: Key,           titleKey: 'err.INVALID_PIN.title',        textKey: 'err.INVALID_PIN.text' },
  PIN_USED:           { Icon: Ban,           titleKey: 'err.PIN_USED.title',           textKey: 'err.PIN_USED.text' },
  PIN_EXPIRED:        { Icon: Timer,         titleKey: 'err.PIN_EXPIRED.title',        textKey: 'err.PIN_EXPIRED.text' },
};

const PROMO_ERRORS = {
  NOT_FOUND:           { Icon: Link2,        titleKey: 'promo.NOT_FOUND.title',           textKey: 'promo.NOT_FOUND.text' },
  PROMO_INACTIVE:      { Icon: PauseCircle,  titleKey: 'promo.PROMO_INACTIVE.title',      textKey: 'promo.PROMO_INACTIVE.text' },
  PROMO_EXPIRED:       { Icon: Clock,        titleKey: 'promo.PROMO_EXPIRED.title',       textKey: 'promo.PROMO_EXPIRED.text' },
  PROMO_EXHAUSTED:     { Icon: XCircle,      titleKey: 'promo.PROMO_EXHAUSTED.title',     textKey: 'promo.PROMO_EXHAUSTED.text' },
  TOO_FAR:             { Icon: MapPin,       titleKey: 'promo.TOO_FAR.title',             textKey: 'promo.TOO_FAR.text' },
  ALREADY_CLAIMED:     { Icon: CheckCircle,  titleKey: 'promo.ALREADY_CLAIMED.title',     textKey: 'promo.ALREADY_CLAIMED.text' },
  COOLDOWN:            { Icon: Timer,        titleKey: 'promo.COOLDOWN.title',            textKey: 'promo.COOLDOWN.text' },
  DAILY_LIMIT_REACHED: { Icon: Ban,          titleKey: 'promo.DAILY_LIMIT_REACHED.title', textKey: 'promo.DAILY_LIMIT_REACHED.text' },
  USER_BANNED:         { Icon: Ban,          titleKey: 'promo.USER_BANNED.title',         textKey: 'promo.USER_BANNED.text' },
};

// ─── Particle burst (25 particles, randomized) ───────────────────────────────

const PARTICLE_COLORS = [C.geo, C.green, C.gold, '#fff', C.geo, C.geo];
// milestone bonus amount comes from API (data.streakInfo.milestoneBonus)

function genParticles() {
  return Array.from({ length: 25 }, (_, i) => {
    const angle = (i / 25) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    const dist  = 70 + Math.random() * 130;
    return {
      tx:    `${(Math.cos(angle) * dist).toFixed(1)}px`,
      ty:    `${(Math.sin(angle) * dist).toFixed(1)}px`,
      delay: `${(i * 0.022).toFixed(3)}s`,
      color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
      size:  5 + Math.random() * 9,
      dur:   0.75 + Math.random() * 0.45,
    };
  });
}

const PARTICLES = genParticles();

// ─── Component ────────────────────────────────────────────────────────────────

export default function Checkin() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token      = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const isPromo    = useMemo(() => searchParams.get('promo') === '1', [searchParams]);
  const isGeohunt  = useMemo(() => searchParams.get('geohunt') === '1', [searchParams]);

  const { lat, lng, error: locError, loading: locLoading, retry: retryGeo } = useLocation();

  const [status,       setStatus]       = useState('loading');
  const [reward,       setReward]       = useState(null);
  const [errInfo,      setErrInfo]      = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [promoInfo,    setPromoInfo]    = useState(null);
  const [huntInfo,     setHuntInfo]     = useState(null);
  const [pin,          setPin]          = useState('');
  const [pinError,     setPinError]     = useState('');
  const [rawErrorCode, setRawErrorCode] = useState(null);
  const [showBurst,          setShowBurst]          = useState(false);
  const [showWave,           setShowWave]           = useState(false);
  const [streakMilestone,    setStreakMilestone]    = useState(null);
  const [checkinData,        setCheckinData]        = useState(null);
  const pinRef = useRef(null);
  const sent   = useRef(false);

  const rarity = promoInfo?.rarity;
  const RC     = RARITY[rarity] || null;

  // ── Mount log ──────────────────────────────────────────────────────────────
  useEffect(() => {
    clog('[CHECKIN:MOUNT] token:', token, '| isPromo:', isPromo, '| isGeohunt:', isGeohunt, '| href:', window.location.href);
    clog('[CHECKIN:RENDER] (logged post-mount) window.location:', window.location.href);
    return () => clog('[CHECKIN:UNMOUNT]');
  }, []);

  // ── Fetch info ──────────────────────────────────────────────────────────────

  useEffect(() => {
    clog('[CHECKIN:FETCH_INFO] token:', token, '| isPromo:', isPromo, '| isGeohunt:', isGeohunt);
    if (!token) {
      cwarn('[CHECKIN:FETCH_INFO] no token — showing error');
      setErrInfo({ Icon: Link2, titleKey: 'err.NO_TOKEN.title', textKey: 'err.NO_TOKEN.text' });
      setStatus('error');
      return;
    }

    // Info endpoints are public — use plain fetch, no initData wait
    if (isGeohunt) {
      const url = `${API_BASE}/api/geohunt/info?token=${encodeURIComponent(token)}`;
      clog('[CHECKIN:FETCH_INFO] geohunt GET', url);
      fetch(url)
        .then(r => r.json().then(d => ({ ok: r.ok, status: r.status, data: d })))
        .then(({ ok, status: s, data }) => {
          clog('[CHECKIN:FETCH_INFO] geohunt response status:', s, '| ok:', ok, '| data:', JSON.stringify(data));
          if (!ok) {
            const GEOHUNT_ERRORS = {
              NOT_FOUND:        { Icon: Link2,        titleKey: 'err.INVALID_QR_TOKEN.title', textKey: 'err.INVALID_QR_TOKEN.text' },
              HUNT_INACTIVE:    { Icon: PauseCircle,  titleKey: 'promo.PROMO_INACTIVE.title', textKey: 'promo.PROMO_INACTIVE.text' },
              HUNT_EXPIRED:     { Icon: Clock,        titleKey: 'promo.PROMO_EXPIRED.title',  textKey: 'promo.PROMO_EXPIRED.text' },
              HUNT_NOT_STARTED: { Icon: Timer,        titleKey: 'promo.PROMO_INACTIVE.title', textKey: 'promo.PROMO_INACTIVE.text' },
              CODE_USED:        { Icon: XCircle,      titleKey: 'promo.PROMO_EXHAUSTED.title', textKey: 'promo.PROMO_EXHAUSTED.text' },
              INTERNAL_ERROR:   { Icon: Wifi,         titleKey: 'err.UNKNOWN.title',           textKey: 'err.UNKNOWN.text' },
            };
            const code = data?.error || 'UNKNOWN';
            cwarn('[CHECKIN:FETCH_INFO] geohunt error code:', code);
            setErrInfo(GEOHUNT_ERRORS[code] || { Icon: XCircle, titleKey: 'err.UNKNOWN.title', textKey: 'err.UNKNOWN.text' });
            setStatus('error');
          } else {
            clog('[CHECKIN:FETCH_INFO] geohunt info OK, setting huntInfo');
            setHuntInfo(data);
          }
        })
        .catch(e => {
          cerr('[CHECKIN:FETCH_INFO] geohunt fetch exception:', e.message);
          setErrInfo({ Icon: Wifi, titleKey: 'err.NO_CONNECTION.title', textKey: 'err.NO_CONNECTION.text' });
          setStatus('error');
        });
      return;
    }

    const endpoint = isPromo
      ? `/api/promo/info?token=${encodeURIComponent(token)}`
      : `/api/checkin/info?token=${encodeURIComponent(token)}`;

    const fullUrl = `${API_BASE}${endpoint}`;
    clog('[CHECKIN:FETCH_INFO] GET', fullUrl);
    fetch(fullUrl)
      .then(r => r.json().then(d => ({ ok: r.ok, status: r.status, data: d })))
      .then(({ ok, status: s, data }) => {
        clog('[CHECKIN:FETCH_INFO] response status:', s, '| ok:', ok, '| data:', JSON.stringify(data));
        if (!ok) {
          const code = data?.error || 'UNKNOWN';
          cwarn('[CHECKIN:FETCH_INFO] error code:', code);
          const map = isPromo ? PROMO_ERRORS : ERRORS;
          setErrInfo(map[code] || { Icon: XCircle, titleKey: 'err.UNKNOWN.title', textKey: 'err.UNKNOWN.text' });
          setStatus('error');
        } else {
          if (isPromo) { clog('[CHECKIN:FETCH_INFO] promo info OK'); setPromoInfo(data); }
          else         { clog('[CHECKIN:FETCH_INFO] business info OK'); setBusinessInfo(data); }
        }
      })
      .catch(e => {
        cerr('[CHECKIN:FETCH_INFO] fetch exception:', e.message);
        setErrInfo({ Icon: Wifi, titleKey: 'err.NO_CONNECTION.title', textKey: 'err.NO_CONNECTION.text' });
        setStatus('error');
      });
  }, [token, isPromo, isGeohunt]);

  // ── Auto-submit when ready (business + promo + geohunt) ─────────────────────

  useEffect(() => {
    const info = isPromo ? promoInfo : isGeohunt ? huntInfo : businessInfo;
    clog('[CHECKIN:AUTOSUBMIT_EFFECT] isGeohunt:', isGeohunt, '| sent:', sent.current, '| status:', status, '| info:', !!info, '| locLoading:', locLoading, '| lat:', lat, '| lng:', lng, '| locError:', locError);
    if (sent.current || status === 'error') return;
    if (!info) return;

    // GeoHunt: location not needed for claim validation — use location resolve as
    // timing gate so the authenticated POST fires after CORS preflight has settled.
    if (isGeohunt) {
      if (locLoading && lat == null && lng == null && !locError) {
        // Location still in-flight — bail out after 3s and proceed anyway
        const bail = setTimeout(() => {
          if (!sent.current) {
            clog('[CHECKIN:AUTOSUBMIT_EFFECT] geohunt: location timeout → doGeohunt()');
            doGeohunt();
          }
        }, 3000);
        return () => clearTimeout(bail);
      }
      if (!sent.current) {
        clog('[CHECKIN:AUTOSUBMIT_EFFECT] → calling doGeohunt()');
        doGeohunt();
      }
      return;
    }

    if (locError) {
      setErrInfo({
        Icon: MapPin,
        titleKey: locError === 'denied_hard' ? 'err.GEO_DENIED_HARD.title' : 'err.GEO_DENIED.title',
        textKey:  locError === 'denied_hard' ? 'err.GEO_DENIED_HARD.text'  : 'err.GEO_DENIED.text',
        showRetry: locError === 'denied',
        showSettings: locError === 'denied_hard',
      });
      setStatus('error');
      return;
    }
    // If geolocation is still pending, set a 12s bail-out so we don't spin forever
    if (locLoading || lat == null || lng == null) {
      const bail = setTimeout(() => {
        if (sent.current) return;
        setErrInfo({
          Icon: MapPin,
          titleKey: 'err.GEO_DENIED.title',
          textKey:  'err.GEO_DENIED.text',
          showRetry: true,
        });
        setStatus('error');
      }, 12000);
      return () => clearTimeout(bail);
    }

    if (!isPromo && businessInfo?.requiresPin) {
      clog('[CHECKIN:AUTOSUBMIT_EFFECT] → requiresPin, showing PIN screen');
      setStatus('pin');
      setTimeout(() => pinRef.current?.focus(), 300);
    } else {
      clog('[CHECKIN:AUTOSUBMIT_EFFECT] → calling', isPromo ? 'doPromo()' : 'doCheckin(null)');
      isPromo ? doPromo() : doCheckin(null);
    }
  }, [businessInfo, promoInfo, huntInfo, locLoading, locError, lat, lng]);

  // ── Checkin (business) ──────────────────────────────────────────────────────

  async function doCheckin(pinValue) {
    clog('[CHECKIN:DO_CHECKIN] token:', token, '| lat:', lat, '| lng:', lng, '| pin:', pinValue ? '***' : null);
    if (sent.current) return;
    sent.current = true;
    setStatus('submitting');
    try {
      const r = await apiFetch('/api/checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qrToken: token, lat, lng,
          pin: pinValue || undefined,
          campaignId: businessInfo?.campaignId || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      clog('[CHECKIN:DO_CHECKIN] response status:', r.status, '| data:', JSON.stringify(data));
      if (!r.ok) {
        const code = data?.error || 'UNKNOWN';
        cwarn('[CHECKIN:DO_CHECKIN] error code:', code, '| status:', r.status, '| data:', JSON.stringify(data));
        setRawErrorCode(`${r.status} ${code}`);
        if (['PIN_REQUIRED', 'INVALID_PIN', 'PIN_USED', 'PIN_EXPIRED'].includes(code)) {
          sent.current = false;
          const pinErrKey = (ERRORS[code] || {}).textKey || 'err.UNKNOWN.text';
          setPinError(t(pinErrKey));
          setStatus('pin');
          setPin('');
        } else {
          setErrInfo(ERRORS[code] || { Icon: XCircle, titleKey: 'err.UNKNOWN.title', textKey: 'err.UNKNOWN.text' });
          setStatus('error');
        }
      } else {
        clog('[CHECKIN:DO_CHECKIN] SUCCESS reward:', data.reward);
        setReward(data.reward);
        setCheckinData(data);
        setStatus('success');
        headerCache.reset();
        setShowBurst(true);
        setShowWave(true);
        setTimeout(() => setShowBurst(false), 1400);
        setTimeout(() => setShowWave(false), 900);
        const ms = data.streakInfo?.projected;
        if ([7, 14, 30].includes(ms)) {
          setTimeout(() => {
            setStreakMilestone(ms);
            setTimeout(() => tg?.HapticFeedback?.notificationOccurred('success'), 100);
          }, 500);
        }
      }
    } catch (e) {
      cerr('[CHECKIN:DO_CHECKIN:CATCH]', e?.message || String(e));
      sent.current = false;
      setErrInfo({ Icon: Wifi, titleKey: 'err.NO_CONNECTION.title', textKey: 'err.NO_CONNECTION.text' });
      setStatus('error');
    }
  }

  // ── Promo claim ─────────────────────────────────────────────────────────────

  async function doPromo() {
    if (sent.current) return;
    sent.current = true;
    setStatus('submitting');
    try {
      const r = await apiFetch('/api/promo/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, lat, lng }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const code = data?.error || 'UNKNOWN';
        setErrInfo(PROMO_ERRORS[code] || { Icon: XCircle, titleKey: 'err.UNKNOWN.title', textKey: 'err.UNKNOWN.text' });
        setStatus('error');
      } else {
        setReward(data.reward);
        setPromoInfo(prev => ({ ...prev, rarity: data.rarity, title: data.title }));
        setStatus('success');
        setShowBurst(true);
        setShowWave(true);
        setTimeout(() => setShowBurst(false), 1400);
        setTimeout(() => setShowWave(false), 900);
      }
    } catch (e) {
      cerr('[CHECKIN:DO_PROMO:CATCH]', e?.message || String(e));
      sent.current = false;
      setErrInfo({ Icon: Wifi, titleKey: 'err.NO_CONNECTION.title', textKey: 'err.NO_CONNECTION.text' });
      setStatus('error');
    }
  }

  // ── GeoHunt claim ───────────────────────────────────────────────────────────

  async function doGeohunt() {
    clog('[CHECKIN:DO_GEOHUNT] token:', token);
    if (sent.current) return;
    sent.current = true;
    setStatus('submitting');

    const GEOHUNT_CLAIM_ERRORS = {
      CODE_USED:     { Icon: XCircle,     titleKey: 'promo.PROMO_EXHAUSTED.title', textKey: 'promo.PROMO_EXHAUSTED.text' },
      HUNT_INACTIVE: { Icon: PauseCircle, titleKey: 'promo.PROMO_INACTIVE.title',  textKey: 'promo.PROMO_INACTIVE.text' },
      HUNT_EXPIRED:  { Icon: Clock,       titleKey: 'promo.PROMO_EXPIRED.title',   textKey: 'promo.PROMO_EXPIRED.text' },
      USER_BANNED:   { Icon: Ban,         titleKey: 'promo.USER_BANNED.title',     textKey: 'promo.USER_BANNED.text'   },
    };

    // Use GET with query params — avoids CORS preflight entirely (no custom headers).
    // initData goes in the query string; server reads it from req.query.initdata.
    try {
      const initData = await waitForInitData(5000);
      const qs = new URLSearchParams({ token });
      if (initData) qs.set('initdata', initData);
      clog('[CHECKIN:DO_GEOHUNT] GET claim | initData:', initData ? `ok(${initData.length}ch)` : 'EMPTY');
      const r = await fetch(`${API_BASE}/api/geohunt/claim?${qs.toString()}`);
      const data = await r.json().catch(() => ({}));
      clog('[CHECKIN:DO_GEOHUNT] status:', r.status, '| data:', JSON.stringify(data));
      if (!r.ok) {
        const code = data?.error || 'UNKNOWN';
        if (code === 'ALREADY_CLAIMED_BY_YOU') {
          setReward(data.reward ?? huntInfo?.reward ?? 0);
          setHuntInfo(prev => ({ ...prev, huntTitle: data.huntTitle || prev?.huntTitle }));
          setStatus('success');
          tg?.HapticFeedback?.notificationOccurred('success');
          return;
        }
        setErrInfo(GEOHUNT_CLAIM_ERRORS[code] || { Icon: XCircle, titleKey: 'err.UNKNOWN.title', textKey: 'err.UNKNOWN.text' });
        setStatus('error');
        return;
      }
      setReward(data.reward);
      setHuntInfo(prev => ({ ...prev, huntTitle: data.huntTitle }));
      setStatus('success');
      setShowBurst(true);
      setShowWave(true);
      setTimeout(() => setShowBurst(false), 1400);
      setTimeout(() => setShowWave(false), 900);
      tg?.HapticFeedback?.notificationOccurred('success');
      return;
    } catch (e) {
      cerr(`[CHECKIN:DO_GEOHUNT:CATCH] name:${e?.name} | msg:${e?.message || String(e)}`);
    }

    sent.current = false;
    setErrInfo({ Icon: Wifi, titleKey: 'err.NO_CONNECTION.title', textKey: 'err.NO_CONNECTION.text' });
    setStatus('error');
  }

  function handlePinSubmit(e) {
    e.preventDefault();
    if (pin.length < 4) { setPinError(t('checkin.pin.min_digits')); return; }
    setPinError('');
    doCheckin(pin);
  }

  // ── Loading spinner color ────────────────────────────────────────────────────

  const spinColor = RC ? RC.color : C.geo;
  const spinBg    = RC ? RC.glow  : C.geoDim;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
      background: C.bg,
      textAlign: 'center',
      transition: 'background 0.6s ease',
    }}>
      {/* WAVE RISE */}
      {showWave && (
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 198,
          background: `linear-gradient(0deg, rgba(143,174,123,0.18) 0%, rgba(143,174,123,0.06) 60%, transparent 100%)`,
          animation: 'waveRise 0.85s ease-out both',
        }} />
      )}

      {/* PARTICLE BURST — 25 particles */}
      {showBurst && (
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {PARTICLES.map((p, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: p.size, height: p.size, borderRadius: '50%',
              background: RC ? RC.color : p.color,
              boxShadow: `0 0 ${p.size * 1.2}px ${RC ? RC.color : p.color}`,
              '--tx': p.tx, '--ty': p.ty,
              animation: `coinBurst ${p.dur}s ${p.delay} cubic-bezier(0.22,0.61,0.36,1) forwards`,
            }} />
          ))}
        </div>
      )}

      {/* STREAK MILESTONE OVERLAY */}
      {streakMilestone && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: C.bg,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px 24px',
          animation: 'pageEnter 0.35s cubic-bezier(0.22,1,0.36,1) both',
        }}>
          {/* Rays */}
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: `rotate(${i * 45}deg)`,
              transformOrigin: '0 0',
              width: 2,
            }}>
              <div style={{
                width: 3, height: 90,
                background: `linear-gradient(to top, ${C.geo}cc, transparent)`,
                borderRadius: 2,
                marginLeft: -1.5,
                marginTop: -130,
                transformOrigin: 'bottom center',
                animation: `rayBurst 1s cubic-bezier(0.22,1,0.36,1) ${i * 0.05}s both`,
              }} />
            </div>
          ))}

          {/* Big streak number */}
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 110, fontWeight: 900, letterSpacing: -5,
            color: C.geo, lineHeight: 1,
            textShadow: `0 0 40px ${C.geoGl}, 0 0 80px ${C.geoDim}`,
            animation: 'streakPop 0.65s cubic-bezier(0.22,1,0.36,1) both',
          }}>
            {streakMilestone}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: 2, marginTop: 4, animation: 'fadeUp 0.4s 0.3s ease both', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            {t('checkin.milestone.streak')}
            <Flame size={13} color={C.orange} strokeWidth={2} />
          </div>

          {/* Bonus card */}
          <div style={{
            marginTop: 28,
            background: C.geoDim, border: `1px solid ${C.geoGl}`,
            borderRadius: 18, padding: '18px 32px', textAlign: 'center',
            animation: 'fadeUp 0.4s 0.45s ease both',
          }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.geo, fontWeight: 900, fontSize: 32, letterSpacing: -1 }}>
              +{(checkinData?.streakInfo?.milestoneBonus || 0).toLocaleString('ru-RU')} GEO
            </div>
            <div style={{ color: C.t3, fontSize: 13, marginTop: 4 }}>{t('checkin.milestone.bonus')}</div>
          </div>

          <RippleButton
            onClick={() => setStreakMilestone(null)}
            style={{
              marginTop: 32,
              background: C.geo, color: C.bg,
              border: 'none', padding: '14px 40px',
              borderRadius: 14, fontWeight: 800, fontSize: 16,
              cursor: 'pointer', letterSpacing: 0.5,
              fontFamily: "'Barlow Condensed', sans-serif",
              animation: 'fadeUp 0.4s 0.6s ease both',
            }}
          >
            {t('checkin.milestone.continue')}
          </RippleButton>
        </div>
      )}

      {/* LOADING / SUBMITTING */}
      {(status === 'loading' || status === 'submitting') && (
        <>
          <div style={{ position: 'relative', width: 96, height: 96, marginBottom: 28 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: spinBg, animation: 'ripple 1.6s ease-out infinite' }} />
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: spinBg, animation: 'ripple 1.6s ease-out 0.5s infinite' }} />
            <div style={{
              position: 'relative', zIndex: 1,
              width: 96, height: 96, borderRadius: '50%',
              background: C.surf, border: `1px solid ${spinColor}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MapPin size={38} color={spinColor} strokeWidth={1.75} />
            </div>
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 10, color: C.t1 }}>
            {status === 'submitting'
              ? (isGeohunt ? t('checkin.geohunt.activating') : isPromo ? t('checkin.submitting_promo') : t('checkin.submitting_biz'))
              : t('checkin.loading')}
          </div>
          <div style={{ color: C.t3, fontSize: 15, lineHeight: 1.5 }}>
            {status === 'submitting'
              ? t('checkin.wait')
              : isGeohunt
                ? t('checkin.geohunt.checking')
                : locLoading
                  ? t('checkin.locating')
                  : t('checkin.verifying')}
          </div>
          {isPromo && promoInfo && status === 'loading' && (
            <div style={{
              marginTop: 24, padding: '12px 20px', borderRadius: 14,
              background: RC ? RC.bg : 'transparent',
              border: `1px solid ${RC ? RC.color + '40' : 'transparent'}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: RC ? RC.color : C.t3, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                {RC ? RC.label : 'Promo'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.t1 }}>{promoInfo.title}</div>
              <div style={{ fontSize: 13, color: C.t3, marginTop: 2 }}>+{formatGeo(promoInfo.reward)} GEO</div>
            </div>
          )}
        </>
      )}

      {/* PIN INPUT (business only) */}
      {status === 'pin' && businessInfo && (
        <div style={{ width: '100%', maxWidth: 360, animation: 'fadeUp 0.4s ease both' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', background: G.gold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', boxShadow: `0 6px 28px ${C.goldGl}`,
            animation: 'pop 0.45s cubic-bezier(0.22,1,0.36,1)',
          }}>
            <Lock size={38} color="#1a0800" strokeWidth={2} />
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 6, color: C.t1 }}>{t('checkin.pin.title')}</div>
          <div style={{ color: C.purpleL, fontSize: 15, marginBottom: 6, fontWeight: 600 }}>{businessInfo.businessName}</div>
          <div style={{ color: C.t3, fontSize: 14, marginBottom: 28, lineHeight: 1.4 }}>
            {t('checkin.pin.ask')}
          </div>
          <form onSubmit={handlePinSubmit}>
            <input
              ref={pinRef}
              value={pin}
              onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setPinError(''); }}
              inputMode="numeric"
              placeholder="• • • • • •"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '18px', borderRadius: 16,
                border: `1px solid ${pinError ? C.red : pin.length >= 4 ? 'rgba(201,123,71,0.45)' : C.b2}`,
                background: C.surf, color: C.t1,
                fontSize: 28, fontWeight: 700, textAlign: 'center', letterSpacing: 10,
                outline: 'none', transition: 'border-color 0.15s', marginBottom: 12,
                fontFamily: "'Barlow Condensed', -apple-system, sans-serif",
              }}
            />
            {pinError && (
              <div style={{
                background: C.redFt, color: C.red, borderRadius: 10,
                padding: '10px 12px', fontSize: 14, fontWeight: 600,
                marginBottom: 16, border: `1px solid rgba(239,68,68,0.2)`,
                display: 'flex', alignItems: 'center', gap: 7,
              }}>
                <AlertTriangle size={15} color={C.red} strokeWidth={2} style={{ flexShrink: 0 }} />
                {pinError}
              </div>
            )}
            <button type="submit" disabled={pin.length < 4} style={{
              width: '100%',
              background: pin.length >= 4 ? C.geo : C.cardHi,
              color: pin.length >= 4 ? C.bg : C.t3,
              border: `1px solid ${pin.length >= 4 ? 'transparent' : C.b2}`,
              padding: '15px', borderRadius: 13,
              fontWeight: 700, fontSize: 15,
              cursor: pin.length >= 4 ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}>
              {t('checkin.pin.confirm')}
            </button>
          </form>
          {businessInfo.reward > 0 && (
            <div style={{ marginTop: 22, color: C.t3, fontSize: 13 }}>
              {t('checkin.pin.reward')} <strong style={{ color: C.purpleL }}>+{formatGeo(businessInfo.reward)} GEO</strong>
            </div>
          )}
        </div>
      )}

      {/* SUCCESS */}
      {status === 'success' && (() => {
        // Derive accent based on mode
        const accentColor = isGeohunt ? C.gold : isPromo && RC ? RC.color : C.geo;
        const accentBg    = isGeohunt ? C.goldFt : isPromo && RC ? RC.bg : C.geoDim;
        const accentBorder= isGeohunt ? C.goldGl  : isPromo && RC ? RC.color + '30' : C.geoGl;
        const accentGrad  = isGeohunt
          ? 'linear-gradient(135deg,#B45309,#FBBF24)'
          : isPromo && RC ? RC.grad : G.emerald;
        const accentGlow  = isGeohunt ? C.goldGl : isPromo && RC ? RC.glow : undefined;

        return (
          <>
            {/* GeoHunt badge */}
            {isGeohunt && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 20,
                background: C.goldFt, border: `1px solid ${C.goldGl}`,
                marginBottom: 20, animation: 'fadeUp 0.3s ease both',
              }}>
                <Star size={13} color={C.gold} strokeWidth={2} />
                <span style={{ fontSize: 12, fontWeight: 800, color: C.gold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  GeoHunt
                </span>
              </div>
            )}

            {/* Rarity badge for promo */}
            {!isGeohunt && isPromo && RC && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 20,
                background: RC.bg, border: `1px solid ${RC.color}40`,
                marginBottom: 20, animation: 'fadeUp 0.3s ease both',
              }}>
                <RC.Icon size={13} color={RC.color} strokeWidth={2} />
                <span style={{ fontSize: 12, fontWeight: 800, color: RC.color, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  {RC.label}
                </span>
              </div>
            )}

            {/* Success icon */}
            <div style={{
              width: 116, height: 116, borderRadius: '50%',
              background: accentGrad,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 32,
              boxShadow: accentGlow ? `0 0 40px ${accentGlow}, 0 0 80px ${accentGlow}` : undefined,
              animation: 'pop 0.55s cubic-bezier(0.22,1,0.36,1), successGlow 2.5s 0.6s ease-in-out infinite',
            }}>
              <CheckCircle size={56} color="#fff" strokeWidth={2.5} />
            </div>

            <div style={{ fontWeight: 900, fontSize: 28, marginBottom: 6, color: C.t1, letterSpacing: -0.5, animation: 'fadeUp 0.4s 0.2s both' }}>
              {isGeohunt ? t('checkin.geohunt.found') : isPromo ? t('checkin.success_promo') : t('checkin.success_biz')}
            </div>
            {isGeohunt && huntInfo?.huntTitle && (
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 4, fontWeight: 700, animation: 'fadeUp 0.4s 0.22s both' }}>
                {huntInfo.huntTitle}
              </div>
            )}
            {!isGeohunt && isPromo && promoInfo?.title && (
              <div style={{ color: accentColor, fontSize: 14, marginBottom: 4, fontWeight: 700, animation: 'fadeUp 0.4s 0.22s both' }}>
                {promoInfo.title}
              </div>
            )}
            <div style={{ color: C.t3, fontSize: 14, marginBottom: 32, animation: 'fadeUp 0.4s 0.25s both' }}>
              {t('checkin.credited')}
            </div>

            {/* Reward card */}
            <div style={{
              background: accentBg,
              border: `1px solid ${accentBorder}`,
              borderRadius: 24, padding: '26px 44px', marginBottom: 36,
              animation: 'fadeUp 0.4s 0.3s both',
              boxShadow: accentGlow ? `0 0 32px ${accentGlow}` : undefined,
            }}>
              <div style={{ color: C.t3, fontSize: 11, marginBottom: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {t('checkin.received')}
              </div>
              <div style={{ animation: 'pop 0.5s 0.45s both' }}>
                <div style={{
                  fontSize: 56, fontWeight: 900, letterSpacing: -2, lineHeight: 1,
                  color: accentColor,
                  fontFamily: "'Barlow Condensed', sans-serif",
                  textShadow: accentGlow ? `0 0 20px ${accentGlow}` : undefined,
                }}>
                  +{formatGeo(reward)}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.t2, marginTop: 6 }}>GEO</div>
              </div>
              {checkinData?.newPlaceBonus > 0 && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  marginTop: 10, padding: '4px 12px', borderRadius: 20,
                  background: C.geoDim, border: `1px solid ${C.geoGl}`,
                  animation: 'fadeUp 0.4s 0.55s both',
                }}>
                  <MapPin size={11} color={C.geo} strokeWidth={2} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.geo }}>
                    +{formatGeo(checkinData.newPlaceBonus)} GEO новое место
                  </span>
                </div>
              )}
            </div>

            <Link to="/balance" style={{
              display: 'block', width: '100%', maxWidth: 300,
              background: accentGrad,
              color: '#fff', textDecoration: 'none',
              padding: '15px 32px', borderRadius: 13,
              fontWeight: 700, fontSize: 15,
              boxShadow: accentGlow ? `0 6px 24px ${accentGlow}` : undefined,
              animation: 'fadeUp 0.4s 0.45s both',
            }}>
              {t('checkin.go_wallet')}
            </Link>
            <Link to="/" style={{
              display: 'block', marginTop: 18,
              color: C.t3, fontSize: 14,
              textDecoration: 'none', animation: 'fadeUp 0.4s 0.55s both',
            }}>
              {t('checkin.go_home')}
            </Link>
          </>
        );
      })()}

      {/* ERROR */}
      {status === 'error' && errInfo && (
        <>
          <div style={{
            width: 104, height: 104, borderRadius: '50%',
            background: C.surf, border: `1px solid rgba(239,68,68,0.2)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 24, animation: 'pop 0.45s cubic-bezier(0.22,1,0.36,1)',
          }}>
            <errInfo.Icon size={48} color={C.red} strokeWidth={1.5} />
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 10, color: C.t1 }}>
            {t(errInfo.titleKey)}
          </div>
          <div style={{ color: C.t3, fontSize: 15, lineHeight: 1.6, marginBottom: 36, maxWidth: 280, whiteSpace: 'pre-line' }}>
            {t(errInfo.textKey)}
          </div>
          {rawErrorCode && (
            <div style={{
              fontFamily: 'monospace', fontSize: 12, color: C.t3,
              background: C.surf, border: `1px solid ${C.b2}`,
              borderRadius: 8, padding: '6px 12px', marginBottom: 16,
              userSelect: 'all',
            }}>
              {rawErrorCode}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            {errInfo.showRetry && (
              <button onClick={retryGeo} style={{
                background: C.geo, color: C.bg, border: 'none',
                padding: '14px 36px', borderRadius: 16,
                fontWeight: 700, fontSize: 16, cursor: 'pointer',
              }}>
                {t('checkin.geo_allow')}
              </button>
            )}
            <Link to="/" style={{
              background: C.surf, border: `1px solid ${C.b2}`,
              color: C.purpleL, textDecoration: 'none',
              padding: '14px 36px', borderRadius: 16,
              fontWeight: 700, fontSize: 16,
            }}>
              {t('checkin.go_home_btn')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
